import { type ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

interface Props {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
}

export function BottomSheet({ open, onClose, children, title }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 animate-[fadeIn_120ms_ease-out]" onClick={onClose} />
      <div
        className="relative bg-white rounded-t-2xl shadow-xl max-h-[90vh] overflow-y-auto animate-[slideUp_160ms_ease-out]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex justify-center py-2">
          <div className="w-10 h-1.5 bg-slate-300 rounded-full" />
        </div>
        {title && (
          <h2 className="px-4 pb-2 text-base font-semibold text-slate-900">{title}</h2>
        )}
        <div>{children}</div>
      </div>
      <style>
        {`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUp { from { transform: translateY(16px); opacity: 0.9; } to { transform: translateY(0); opacity: 1; } }
        `}
      </style>
    </div>,
    document.body,
  );
}
